import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import type { ResourceKey } from "i18next";
import { addTranslation } from "@/utils/i18n";
import { zodUnsafeValidate } from "@/utils/zod";
import {
  CalculatorContext,
  createCalculatorContext,
} from "./context/CalculatorContext";
import { CalculatorCustomPack } from "./layout/calculator-custom-pack/CalculatorCustomPack";
import { CalculatorDefault } from "./layout/calculator-default/CalculatorDefault";
import { Calc } from "./types";

declare global {
  interface Window {
    calculatorConfig?: Calc.Config;
  }
}

export const createCalculators = async () => {
  if (window.calculatorConfig !== undefined) {
    const config = zodUnsafeValidate(
      Calc.ConfigSchema,
      window.calculatorConfig,
    );

    Object.entries(config.calculators).forEach(([id, calculator]) => {
      const containers = document.querySelectorAll(`[data-calculator="${id}"]`);

      containers.forEach((container) =>
        createCalculator(container, {
          api: config.api,
          links: config.links,
          calculator,
          translation: config.translation,
        }),
      );
    });
  }
};

const createCalculator = async (
  container: Element,
  {
    api,
    links,
    calculator,
    translation,
  }: {
    api: Calc.Api.Endpoints;
    links: Calc.Links.Links;
    calculator: Calc.Calculator.Calculator;
    translation: ResourceKey;
  },
) => {
  const root = createRoot(container);

  addTranslation(translation);

  root.render(
    <StrictMode>
      <CalculatorContext.Provider
        value={createCalculatorContext({
          api,
          links,
          calculator,
        })}
      >
        {(() => {
          switch (calculator.type) {
            case "default":
              return <CalculatorDefault />;

            case "custom-pack":
              return <CalculatorCustomPack />;
          }
        })()}
      </CalculatorContext.Provider>
    </StrictMode>,
  );
};
